import React, { useState, useEffect, useCallback } from 'react';
import { providersService, ProviderWithColocs, ProviderSuggestion, LIBELLES_ENERGIE, type Energie } from '../services/providers.service';
import { colocService, Coloc } from '../services/coloc.service';
import { expenseCategoriesService, ExpenseCategory } from '../services/expense-categories.service';
import '../styles/MyProvidersPage.css';
import { getErrorMessage } from '../utils/error';
import { fractionVersPourcentage, pourcentageVersFraction } from '../utils/format';

const ITEMS_PER_PAGE = 10;

const MyProvidersPage: React.FC = () => {
  // Data
  const [providers, setProviders] = useState<ProviderWithColocs[]>([]);
  const [colocs, setColocs] = useState<Coloc[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [suggestions, setSuggestions] = useState<ProviderSuggestion[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProviderId, setExpandedProviderId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProviderName, setNewProviderName] = useState('');
  const [newExpenseCategoryId, setNewExpenseCategoryId] = useState('');
  const [newRequiresConsumptionPeriod, setNewRequiresConsumptionPeriod] = useState(false);
  // « » : le serveur la déduit du nom quand le nom la dit (GRDF, EDF, Iléo…).
  const [newEnergy, setNewEnergy] = useState<Energie | ''>('');
  const [newSelectedColocIds, setNewSelectedColocIds] = useState<string[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<ProviderSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Delete modal
  const [deleteProviderId, setDeleteProviderId] = useState<string | null>(null);

  // Assign modal
  const [assignProviderId, setAssignProviderId] = useState<string | null>(null);
  const [assignColocId, setAssignColocId] = useState('');
  const [assignPrc, setAssignPrc] = useState('');
  const [assignAmount, setAssignAmount] = useState('');

  // Unassign modal
  const [unassignData, setUnassignData] = useState<{ providerId: string; colocId: string; colocName: string } | null>(null);

  // Edit assignment modal
  const [editAssignment, setEditAssignment] = useState<{
    providerId: string;
    colocId: string;
    colocName: string;
    prc?: number | null;
    amount?: number | null;
  } | null>(null);
  const [editPrc, setEditPrc] = useState('');
  const [editAmount, setEditAmount] = useState('');

  // Action feedback
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await providersService.getAllProviders();
      setProviders(data);
    } catch (err) {
      setError('Impossible de charger les fournisseurs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [, userColocs, cats, sugg] = await Promise.all([
          fetchProviders(),
          colocService.getColocs(),
          expenseCategoriesService.getExpenseCategories(),
          providersService.getSuggestions(),
        ]);
        setColocs(userColocs);
        setCategories(cats);
        setSuggestions(sugg);
      } catch (err) {
        setError('Impossible de charger les données');
      }
    };
    init();
  }, [fetchProviders]);

  // Pagination
  const totalPages = Math.ceil(providers.length / ITEMS_PER_PAGE);
  const currentProviders = providers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // -----------------------------------------------------------------------
  // Create provider
  // -----------------------------------------------------------------------

  const handleNameInput = (value: string) => {
    setNewProviderName(value);
    if (value.length > 0) {
      const matches = suggestions.filter((s) =>
        s.providerName.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: ProviderSuggestion) => {
    setNewProviderName(suggestion.providerName);
    setShowSuggestions(false);
    setNewRequiresConsumptionPeriod(suggestion.requiresConsumptionPeriod);
    setNewEnergy(suggestion.energy ?? '');
    // Match category
    const cat = categories.find(
      (c) => c.name.toLowerCase() === suggestion.expenseCategory.toLowerCase()
    );
    if (cat) setNewExpenseCategoryId(cat.id);
  };

  /** Choisie par le bailleur : un fournisseur de gaz ET d'électricité ne se
   *  devine pas à son nom (#15). « » = non renseignée, hors des graphiques. */
  const handleEnergyChange = async (providerId: string, energy: Energie | '') => {
    setActionError(null);
    try {
      await providersService.updateProvider(providerId, { energy: energy || null });
      await fetchProviders();
    } catch (err) {
      setActionError(getErrorMessage(err, "Erreur lors de l'enregistrement de l'énergie"));
    }
  };

  const handleCreate = async () => {
    if (!newProviderName.trim()) return;
    setActionError(null);
    try {
      const firstColocId = newSelectedColocIds.length > 0 ? newSelectedColocIds[0] : undefined;
      const created = await providersService.createProvider({
        providerName: newProviderName.trim(),
        expenseCategoryId: newExpenseCategoryId || undefined,
        requiresConsumptionPeriod: newRequiresConsumptionPeriod,
        ...(newEnergy ? { energy: newEnergy } : {}),
        colocId: firstColocId,
      });
      // Assign remaining colocs
      for (let i = 1; i < newSelectedColocIds.length; i++) {
        await providersService.assignToColoc(created.id, newSelectedColocIds[i]);
      }
      setShowCreateModal(false);
      setNewProviderName('');
      setNewExpenseCategoryId('');
      setNewRequiresConsumptionPeriod(false);
      setNewEnergy('');
      setNewSelectedColocIds([]);
      await fetchProviders();
    } catch (err) {
      setActionError(getErrorMessage(err, 'Erreur lors de la création'));
    }
  };

  // -----------------------------------------------------------------------
  // Delete provider
  // -----------------------------------------------------------------------

  const handleDelete = async () => {
    if (!deleteProviderId) return;
    setActionError(null);
    try {
      await providersService.deleteProvider(deleteProviderId);
      setDeleteProviderId(null);
      await fetchProviders();
    } catch (err) {
      setActionError(getErrorMessage(err, 'Erreur lors de la suppression'));
    }
  };

  // -----------------------------------------------------------------------
  // Assign to coloc
  // -----------------------------------------------------------------------

  const getAvailableColocs = (providerId: string) => {
    const provider = providers.find((p) => p.id === providerId);
    if (!provider) return colocs;
    const assignedIds = new Set(provider.colocs.map((c) => c.colocId));
    return colocs.filter((c) => !assignedIds.has(c.id));
  };

  const handleAssign = async () => {
    if (!assignProviderId || !assignColocId) return;
    setActionError(null);
    try {
      await providersService.assignToColoc(
        assignProviderId,
        assignColocId,
        assignPrc ? pourcentageVersFraction(assignPrc) : null,
        assignAmount ? parseFloat(assignAmount) : null
      );
      setAssignProviderId(null);
      setAssignColocId('');
      setAssignPrc('');
      setAssignAmount('');
      await fetchProviders();
    } catch (err) {
      setActionError(getErrorMessage(err, "Erreur lors de l'assignation"));
    }
  };

  // -----------------------------------------------------------------------
  // Unassign from coloc
  // -----------------------------------------------------------------------

  const handleUnassign = async () => {
    if (!unassignData) return;
    setActionError(null);
    try {
      await providersService.unassignFromColoc(unassignData.providerId, unassignData.colocId);
      setUnassignData(null);
      await fetchProviders();
    } catch (err) {
      setActionError(getErrorMessage(err, 'Erreur lors du retrait'));
    }
  };

  // -----------------------------------------------------------------------
  // Edit coloc settings
  // -----------------------------------------------------------------------

  const openEditAssignment = (
    providerId: string,
    colocId: string,
    colocName: string,
    prc?: number | null,
    amount?: number | null
  ) => {
    setEditAssignment({ providerId, colocId, colocName, prc, amount });
    setEditPrc(prc != null ? String(fractionVersPourcentage(prc)) : '');
    setEditAmount(amount != null ? String(amount) : '');
    setActionError(null);
  };

  const handleEditAssignment = async () => {
    if (!editAssignment) return;
    setActionError(null);
    try {
      await providersService.updateColocSettings(
        editAssignment.providerId,
        editAssignment.colocId,
        editPrc ? pourcentageVersFraction(editPrc) : null,
        editAmount ? parseFloat(editAmount) : null
      );
      setEditAssignment(null);
      await fetchProviders();
    } catch (err) {
      setActionError(getErrorMessage(err, 'Erreur lors de la modification'));
    }
  };

  // -----------------------------------------------------------------------
  // Validation
  // -----------------------------------------------------------------------

  const hasBothPrcAndAmount = (prc: string, amount: string) =>
    prc !== '' && amount !== '' && parseFloat(prc) > 0 && parseFloat(amount) > 0;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (loading) return <div className="loading-indicator">Chargement...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="my-providers-container">
      <div className="my-providers-header">
        <h2>Mes Fournisseurs</h2>
        <button className="add-button" onClick={() => { setShowCreateModal(true); setActionError(null); }}>
          + Ajouter un fournisseur
        </button>
      </div>

      {providers.length === 0 ? (
        <div className="empty-message">Aucun fournisseur. Cliquez sur "Ajouter" pour en créer un.</div>
      ) : (
        <>
          {/* Table header */}
          <div className="mp-list">
            <div className="mp-list-header">
              <div className="mp-col mp-col-name">Nom</div>
              <div className="mp-col mp-col-category">Catégorie</div>
              <div className="mp-col mp-col-colocs">Colocations</div>
              <div className="mp-col mp-col-actions">Actions</div>
            </div>

            {currentProviders.map((provider) => (
              <React.Fragment key={provider.id}>
                {/* Provider row */}
                <div className="mp-row">
                  <div className="mp-col mp-col-name">{provider.providerName}</div>
                  <div className="mp-col mp-col-category">
                    {provider.expenseCategory?.name || '-'}
                  </div>
                  <div className="mp-col mp-col-colocs">
                    {provider.colocs.length === 0 ? (
                      <span className="no-coloc-text">(aucune)</span>
                    ) : (
                      <div className="coloc-badges">
                        {provider.colocs.map((c) => (
                          <span key={c.colocId} className="coloc-badge">{c.colocName}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mp-col mp-col-actions">
                    <button
                      className="chevron-button"
                      title="Détails"
                      onClick={() =>
                        setExpandedProviderId(
                          expandedProviderId === provider.id ? null : provider.id
                        )
                      }
                    >
                      {expandedProviderId === provider.id ? '▼' : '▶'}
                    </button>
                    <button
                      className="delete-icon-button"
                      title="Supprimer"
                      onClick={(e) => { e.stopPropagation(); setDeleteProviderId(provider.id); setActionError(null); }}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Expanded panel */}
                {expandedProviderId === provider.id && (
                  <div className="mp-expanded-panel">
                    <div className="provider-details-row">
                      <span className={`consumption-badge ${provider.requiresConsumptionPeriod ? 'active' : ''}`}>
                        {provider.requiresConsumptionPeriod ? '⏱ Période de consommation requise' : 'Pas de période de consommation'}
                      </span>
                      <label className="energie-fournisseur">
                        Énergie{' '}
                        <select
                          aria-label={`Énergie de ${provider.providerName}`}
                          value={provider.energy ?? ''}
                          onChange={(e) => handleEnergyChange(provider.id, e.target.value as Energie | '')}
                        >
                          <option value="">Non renseignée (hors graphiques)</option>
                          {(Object.keys(LIBELLES_ENERGIE) as Energie[]).map((cle) => (
                            <option key={cle} value={cle}>{LIBELLES_ENERGIE[cle]}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    {provider.colocs.length > 0 && (
                      <div className="coloc-assignments-table">
                        <div className="coloc-assignment-header">
                          <div className="ca-col ca-col-name">Colocation</div>
                          <div className="ca-col ca-col-amount">Montant fixe</div>
                          <div className="ca-col ca-col-prc">Pourcentage</div>
                          <div className="ca-col ca-col-actions">Actions</div>
                        </div>
                        {provider.colocs.map((c) => (
                          <div key={c.colocId} className="coloc-assignment-row">
                            <div className="ca-col ca-col-name">{c.colocName}</div>
                            <div className="ca-col ca-col-amount">
                              {c.amountOfInvoice != null ? `${c.amountOfInvoice} €` : '-'}
                            </div>
                            <div className="ca-col ca-col-prc">
                              {c.prcOfInvoice != null ? `${fractionVersPourcentage(c.prcOfInvoice)} %` : '-'}
                            </div>
                            <div className="ca-col ca-col-actions">
                              <button
                                className="edit-small-button"
                                onClick={() =>
                                  openEditAssignment(
                                    provider.id,
                                    c.colocId,
                                    c.colocName,
                                    c.prcOfInvoice,
                                    c.amountOfInvoice
                                  )
                                }
                              >
                                Modifier
                              </button>
                              <button
                                className="remove-small-button"
                                onClick={() =>
                                  setUnassignData({
                                    providerId: provider.id,
                                    colocId: c.colocId,
                                    colocName: c.colocName,
                                  })
                                }
                              >
                                Retirer
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {getAvailableColocs(provider.id).length > 0 && (
                      <button
                        className="assign-coloc-button"
                        onClick={() => {
                          setAssignProviderId(provider.id);
                          setActionError(null);
                        }}
                      >
                        + Assigner à une colocation
                      </button>
                    )}

                    {provider.colocs.length === 0 && getAvailableColocs(provider.id).length === 0 && (
                      <div className="no-coloc-text">Aucune colocation disponible.</div>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={currentPage === i + 1 ? 'active' : ''}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* CREATE MODAL                                                      */}
      {/* ================================================================= */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Ajouter un fournisseur</h3>

            <div className="form-group">
              <label>Nom du fournisseur</label>
              <div className="autocomplete-wrapper">
                <input
                  type="text"
                  value={newProviderName}
                  onChange={(e) => handleNameInput(e.target.value)}
                  onFocus={() => {
                    if (newProviderName.length > 0) {
                      const matches = suggestions.filter((s) =>
                        s.providerName.toLowerCase().includes(newProviderName.toLowerCase())
                      );
                      setFilteredSuggestions(matches);
                      setShowSuggestions(matches.length > 0);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Ex: EDF, Orange..."
                />
                {showSuggestions && (
                  <ul className="suggestions-list">
                    {filteredSuggestions.map((s) => (
                      <li key={s.providerName} onMouseDown={() => handleSuggestionClick(s)}>
                        <strong>{s.providerName}</strong>
                        <span className="suggestion-cat">{s.expenseCategory}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Catégorie de dépense</label>
              <select
                value={newExpenseCategoryId}
                onChange={(e) => setNewExpenseCategoryId(e.target.value)}
              >
                <option value="">-- Aucune --</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="newEnergy">Énergie (graphiques de consommation)</label>
              <select
                id="newEnergy"
                value={newEnergy}
                onChange={(e) => setNewEnergy(e.target.value as Energie | '')}
              >
                <option value="">D&apos;après le nom (GRDF, EDF, Iléo…)</option>
                {(Object.keys(LIBELLES_ENERGIE) as Energie[]).map((cle) => (
                  <option key={cle} value={cle}>{LIBELLES_ENERGIE[cle]}</option>
                ))}
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={newRequiresConsumptionPeriod}
                  onChange={(e) => setNewRequiresConsumptionPeriod(e.target.checked)}
                />
                Nécessite une période de consommation
              </label>
            </div>

            <div className="form-group">
              <label>Assigner aux colocations</label>
              <div className="coloc-checkboxes">
                {colocs.map((c) => (
                  <label key={c.id} className="coloc-checkbox-item">
                    <input
                      type="checkbox"
                      checked={newSelectedColocIds.includes(c.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewSelectedColocIds([...newSelectedColocIds, c.id]);
                        } else {
                          setNewSelectedColocIds(newSelectedColocIds.filter((id) => id !== c.id));
                        }
                      }}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>

            {actionError && <div className="modal-error">{actionError}</div>}

            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setShowCreateModal(false)}>
                Annuler
              </button>
              <button
                className="confirm-button"
                onClick={handleCreate}
                disabled={!newProviderName.trim()}
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* DELETE MODAL                                                      */}
      {/* ================================================================= */}
      {deleteProviderId && (
        <div className="modal-overlay" onClick={() => setDeleteProviderId(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmer la suppression</h3>
            <p>
              Supprimer ce fournisseur ? Cette action supprimera toutes ses affectations aux
              colocations.
            </p>
            {actionError && <div className="modal-error">{actionError}</div>}
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setDeleteProviderId(null)}>
                Annuler
              </button>
              <button className="confirm-button danger" onClick={handleDelete}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* ASSIGN MODAL                                                      */}
      {/* ================================================================= */}
      {assignProviderId && (
        <div className="modal-overlay" onClick={() => setAssignProviderId(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Assigner à une colocation</h3>

            <div className="form-group">
              <label>Colocation</label>
              <select value={assignColocId} onChange={(e) => setAssignColocId(e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {getAvailableColocs(assignProviderId).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Montant fixe (€)</label>
              <input
                type="number"
                step="0.01"
                value={assignAmount}
                onChange={(e) => setAssignAmount(e.target.value)}
                placeholder="Optionnel"
              />
            </div>

            <div className="form-group">
              <label>Pourcentage (%)</label>
              <input
                type="number"
                step="0.01"
                max="100"
                value={assignPrc}
                onChange={(e) => setAssignPrc(e.target.value)}
                placeholder="Optionnel"
              />
            </div>

            {hasBothPrcAndAmount(assignPrc, assignAmount) && (
              <div className="modal-error">
                Vous ne pouvez pas définir à la fois un montant fixe et un pourcentage.
              </div>
            )}

            {actionError && <div className="modal-error">{actionError}</div>}

            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setAssignProviderId(null)}>
                Annuler
              </button>
              <button
                className="confirm-button"
                onClick={handleAssign}
                disabled={!assignColocId || hasBothPrcAndAmount(assignPrc, assignAmount)}
              >
                Assigner
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* UNASSIGN MODAL                                                    */}
      {/* ================================================================= */}
      {unassignData && (
        <div className="modal-overlay" onClick={() => setUnassignData(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Retirer de la colocation</h3>
            <p>
              Retirer ce fournisseur de <strong>{unassignData.colocName}</strong> ?
            </p>
            {actionError && <div className="modal-error">{actionError}</div>}
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setUnassignData(null)}>
                Annuler
              </button>
              <button className="confirm-button danger" onClick={handleUnassign}>
                Retirer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* EDIT ASSIGNMENT MODAL                                             */}
      {/* ================================================================= */}
      {editAssignment && (
        <div className="modal-overlay" onClick={() => setEditAssignment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Modifier — {editAssignment.colocName}</h3>

            <div className="form-group">
              <label>Montant fixe (€)</label>
              <input
                type="number"
                step="0.01"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Pourcentage (%)</label>
              <input
                type="number"
                step="0.01"
                max="100"
                value={editPrc}
                onChange={(e) => setEditPrc(e.target.value)}
              />
            </div>

            {hasBothPrcAndAmount(editPrc, editAmount) && (
              <div className="modal-error">
                Vous ne pouvez pas définir à la fois un montant fixe et un pourcentage.
              </div>
            )}

            {actionError && <div className="modal-error">{actionError}</div>}

            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setEditAssignment(null)}>
                Annuler
              </button>
              <button
                className="confirm-button"
                onClick={handleEditAssignment}
                disabled={hasBothPrcAndAmount(editPrc, editAmount)}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProvidersPage;
